// Pure helpers for the semester planner. Dates are "YYYY-MM-DD" strings and are
// handled in local time so they never shift across timezones.

export const parseDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const formatISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

export const addDays = (s, n) => {
  const d = parseDate(s);
  d.setDate(d.getDate() + n);
  return formatISO(d);
};

export const diffDays = (a, b) =>
  Math.round((parseDate(b) - parseDate(a)) / 864e5);

export const isWeekend = (s) => {
  const w = parseDate(s).getDay();
  return w === 0 || w === 6;
};

const nextWeekdayOf = (s) => {
  let d = addDays(s, 1);
  while (isWeekend(d)) d = addDays(d, 1);
  return d;
};

export const weekdaysBetween = (a, b) => {
  let n = 0;
  for (let d = a; d <= b; d = addDays(d, 1)) if (!isWeekend(d)) n += 1;
  return n;
};

/** Last day of an n-weekday class that starts on `start` (n >= 1). */
export const addWeekdays = (start, n) => {
  let d = start;
  for (let c = 1; c < n; c += 1) d = nextWeekdayOf(d);
  return d;
};

export const formatDay = (s, opts = { month: "short", day: "numeric" }) =>
  parseDate(s).toLocaleDateString("en-US", opts);

export const formatRange = (a, b) =>
  a === b ? formatDay(a) : `${formatDay(a)} – ${formatDay(b)}`;

const sessionsOf = (sc) =>
  Array.isArray(sc?.scheduledDates) ? sc.scheduledDates : [];

/** Sorted unique session dates of a semester class. */
export const classDates = (sc) =>
  [...new Set(sessionsOf(sc).map((s) => s.date).filter(Boolean))].sort();

/** First and last session date, or null when nothing is scheduled. */
export const classSpan = (sc) => {
  const dates = classDates(sc);
  return dates.length
    ? { start: dates[0], end: dates[dates.length - 1] }
    : null;
};

/**
 * Contiguous blocks of session dates. A weekend between two weekday sessions
 * does not break a block; any skipped weekday does.
 */
export const classRuns = (sc) =>
  classDates(sc).reduce((runs, date) => {
    const last = runs[runs.length - 1];
    if (last && (date === addDays(last.end, 1) || date === nextWeekdayOf(last.end))) {
      return [...runs.slice(0, -1), { start: last.start, end: date }];
    }
    return [...runs, { start: date, end: date }];
  }, []);

const toMinutes = (t) => {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
};

// Missing times mean "all day", so they overlap everything on that date.
const timesOverlap = (a, b) => {
  const as = toMinutes(a.startTime);
  const ae = toMinutes(a.endTime);
  const bs = toMinutes(b.startTime);
  const be = toMinutes(b.endTime);
  if ([as, ae, bs, be].some((v) => v === null)) return true;
  return as < be && bs < ae;
};

const sessionsClash = (a, b) =>
  sessionsOf(a).some((sa) =>
    sessionsOf(b).some((sb) => sa.date === sb.date && timesOverlap(sa, sb)),
  );

const roomKey = (sc) => (sc.location || "").trim().toLowerCase();

/**
 * Instructor double-booking is blocking. Room clashes are warnings only,
 * because location is free text.
 */
export const findConflicts = (classes) => {
  const out = [];
  for (let i = 0; i < classes.length; i += 1) {
    for (let j = i + 1; j < classes.length; j += 1) {
      const a = classes[i];
      const b = classes[j];
      if (!sessionsClash(a, b)) continue;
      if (a.instructorId && String(a.instructorId) === String(b.instructorId)) {
        out.push({
          a: a.id,
          b: b.id,
          kind: "instructor",
          blocking: true,
          what: String(a.instructorId),
        });
      }
      if (roomKey(a) && roomKey(a) === roomKey(b)) {
        out.push({
          a: a.id,
          b: b.id,
          kind: "room",
          blocking: false,
          what: a.location.trim(),
        });
      }
    }
  }
  return out;
};

export const readiness = (classes) => {
  const conflicts = findConflicts(classes);
  const unassigned = classes.filter((c) => !c.instructorId);
  const blocking = conflicts.filter((c) => c.blocking);
  const warnings = conflicts.filter((c) => !c.blocking);
  const issueCount = conflicts.length + unassigned.length;
  return {
    conflicts,
    blocking,
    warnings,
    unassigned,
    issueCount,
    ready: issueCount === 0,
  };
};

/** Scheduled session days per instructor id. */
export const instructorLoad = (classes) =>
  classes.reduce((acc, sc) => {
    if (!sc.instructorId) return acc;
    return {
      ...acc,
      [sc.instructorId]: (acc[sc.instructorId] || 0) + classDates(sc).length,
    };
  }, {});
